import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UpgradePromptProvider } from "@/components/UpgradePromptProvider";
import { FeaturesProvider } from "@/context/FeaturesContext";
import { AccessProvider } from "@/context/AccessContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Protected } from "@/components/Protected";
import DashboardLayout from "@/components/DashboardLayout";
import PWAInstallPill from "@/components/PWAInstallPill";
import PasskeyNudge from "@/components/PasskeyNudge";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PasswordExpiryDialog from "@/components/PasswordExpiryDialog";
import Register from "@/pages/Register";

import TenantOverview from "@/pages/TenantOverview";
import TenantDashboardV2 from "@/pages/TenantDashboardV2";
import TenantDataIntegrity from "@/pages/TenantDataIntegrity";
import Clients from "@/pages/Clients";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import CalendarPage from "@/pages/CalendarPage";
import AdminCourtVcLinks from "@/pages/admin/AdminCourtVcLinks";
import AdminMasterData from "@/pages/admin/AdminMasterData";
import Hearings from "@/pages/Hearings";
import Tasks from "@/pages/Tasks";
import Documents from "@/pages/Documents";
import Invoices from "@/pages/Invoices";
import Communications from "@/pages/Communications";
import Automations from "@/pages/Automations";
import AIAssistant from "@/pages/AIAssistant";
import AIDocs from "@/pages/AIDocs";
import Analytics from "@/pages/Analytics";
import Compliance from "@/pages/Compliance";
import AuditLog from "@/pages/AuditLog";
import Referrals from "@/pages/Referrals";
import ReferralLanding from "@/pages/ReferralLanding";
import CourtVcLinks from "@/pages/CourtVcLinks";
import Settings from "@/pages/Settings";
import BillingAutomation from "@/pages/BillingAutomation";
import SignInActivity from "@/pages/SignInActivity";
import BillingPlans from "@/pages/BillingPlans";
import AppAddons from "@/pages/AppAddons";
import ApiKeysPage from "@/pages/ApiKeysPage";
import WhiteLabelPage from "@/pages/WhiteLabelPage";
import PrioritySupportPage from "@/pages/PrioritySupportPage";
import FieldMasksPage from "@/pages/FieldMasksPage";
import InvoiceEmailSettingsPage from "@/pages/InvoiceEmailSettingsPage";
import TenantInvoiceTemplate from "@/pages/TenantInvoiceTemplate";

import AdminOverview from "@/pages/admin/AdminOverview";
import AdminTenants from "@/pages/admin/AdminTenants";
import AdminTenantDetail from "@/pages/admin/AdminTenantDetail";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminTopupPacks from "@/pages/admin/AdminTopupPacks";
import AdminPricingCMS from "@/pages/admin/AdminPricingCMS";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminComms from "@/pages/admin/AdminComms";
import AdminSessions from "@/pages/admin/AdminSessions";
import AdminDeliveryChains from "@/pages/admin/AdminDeliveryChains";
import AdminFeatures from "@/pages/admin/AdminFeatures";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminAIGovernance from "@/pages/admin/AdminAIGovernance";
import AdminAccessDefaults from "@/pages/admin/AdminAccessDefaults";
import AdminEcourts from "@/pages/admin/AdminEcourts";
import AdminScraper from "@/pages/admin/AdminScraper";
import AdminAutomations from "@/pages/admin/AdminAutomations";
import AdminSpendAnalysis from "@/pages/admin/AdminSpendAnalysis";
import IndianKanoonConfig from "@/pages/admin/IndianKanoonConfig";
import VoicesConfig from "@/pages/admin/VoicesConfig";
import AdminChatbot from "@/pages/admin/AdminChatbot";
import AdminLegalPages from "@/pages/admin/AdminLegalPages";
import AdminDocumentation from "@/pages/admin/AdminDocumentation";
import AdminCaseTypePrefixes from "@/pages/admin/AdminCaseTypePrefixes";
import AdminPlatformTeam from "@/pages/admin/AdminPlatformTeam";
import AdminInvoiceTemplate from "@/pages/admin/AdminInvoiceTemplate";
import AdminPlatformInvoiceBranding from "@/pages/admin/AdminPlatformInvoiceBranding";
import AdminSuggestionRules from "@/pages/admin/AdminSuggestionRules";
import AdminPrioritySupport from "@/pages/admin/AdminPrioritySupport";
import AdminEnterpriseControls from "@/pages/admin/AdminEnterpriseControls";
import AcceptInvite from "@/pages/AcceptInvite";
import Documentation from "@/pages/Documentation";
import CauseList from "@/pages/CauseList";
import AdminOnboarding from "@/pages/admin/AdminOnboarding";
import AdminAccounting from "@/pages/admin/AdminAccounting";
import AdminTenantAccounting from "@/pages/admin/AdminTenantAccounting";
import AdminBillingAutomation from "@/pages/admin/AdminBillingAutomation";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import TenantAccounting from "@/pages/TenantAccounting";
import SubscriptionInvoices from "@/pages/SubscriptionInvoices";
import NotificationSettings from "@/pages/NotificationSettings";
import TenantPushSettings from "@/pages/TenantPushSettings";
import AdminPushSettings from "@/pages/admin/AdminPushSettings";
import AdminSystemHealth from "@/pages/admin/AdminSystemHealth";
import AdminDataIntegrity from "@/pages/admin/AdminDataIntegrity";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminBackups from "@/pages/admin/AdminBackups";
import AdminSystem from "@/pages/admin/AdminSystem";
import OnboardingWizard from "@/pages/onboarding/OnboardingWizard";
import LegalPage from "@/pages/public/LegalPage";
import PricingPage from "@/pages/public/PricingPage";
import PlanCompare from "@/pages/public/PlanCompare";
import ContactPage from "@/pages/public/ContactPage";
import CheckoutPage from "@/pages/public/CheckoutPage";
import PaymentResultPage from "@/pages/public/PaymentResultPage";
import EcourtsConfig from "@/pages/EcourtsConfig";
import LegalSearch from "@/pages/LegalSearch";
import Profile from "@/pages/Profile";
import Chat from "@/pages/Chat";
import WalletPage from "@/pages/WalletPage";
import AccessControl from "@/pages/AccessControl";
import MyWeek from "@/pages/MyWeek";
import Staff from "@/pages/Staff";
import Productivity from "@/pages/Productivity";
import { BrandingProvider } from "@/context/BrandingContext";

import PortalLayout from "@/components/PortalLayout";
import PortalOverview from "@/pages/portal/PortalOverview";
import PortalChat from "@/pages/portal/PortalChat";
import { PortalCases, PortalCaseDetail, PortalHearings, PortalInvoices, PortalDocuments } from "@/pages/portal/PortalPages";

// ── Sales & Marketing Module ───────────────────────────────────────────────
import SMLayout from "@/pages/sm/SMLayout";
import SMDashboard from "@/pages/sm/SMDashboard";
import SMPartners from "@/pages/sm/SMPartners";
import SMSettings from "@/pages/sm/SMSettings";
import SMAIGuide from "@/pages/sm/SMAIGuide";
import SMHierarchy from "@/pages/sm/SMHierarchy";
import SMCommissionConfig from "@/pages/sm/SMCommissionConfig";
import SMCommissionLedger from "@/pages/sm/SMCommissionLedger";
import SMStatements from "@/pages/sm/SMStatements";
import SMCRM from "@/pages/sm/SMCRM";
import SMKYC from "@/pages/sm/SMKYC";
import SMReferralLinks from "@/pages/sm/SMReferralLinks";

// ── Partner Portal ─────────────────────────────────────────────────────────
import PartnerLayout from "@/pages/partner/PartnerLayout";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerCommissions from "@/pages/partner/PartnerCommissions";
import PartnerReferrals from "@/pages/partner/PartnerReferrals";
import PartnerProfile from "@/pages/partner/PartnerProfile";
import PartnerRegister from "@/pages/partner/PartnerRegister";
import PartnerLogin from "@/pages/partner/PartnerLogin";
import PartnerLinks from "@/pages/partner/PartnerLinks";
import PartnerKYCStatus from "@/pages/partner/PartnerKYCStatus";
import JoinLink from "@/pages/JoinLink";

function SMProtected({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (!["super_admin", "sm_admin"].includes(user.role)) {
        // Partners go to their portal; everyone else to firm workspace
        return <Navigate to={user.role === "partner" ? "/partner" : "/app"} replace />;
    }
    return children;
}

function PartnerProtected({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/partner/login" replace />;
    if (user.role !== "partner") return <Navigate to="/app" replace />;
    return children;
}
// ──────────────────────────────────────────────────────────────────────────

function HomeRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Landing />;
    const dest = user.role === "super_admin" ? "/admin"
        : user.role === "client_portal" ? "/portal"
        : user.role === "partner" ? "/partner"
        : "/app";
    return <Navigate to={dest} replace />;
}

export default function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
            <FeaturesProvider>
            <AccessProvider>
            <BrandingProvider>
            <BrowserRouter>
                <UpgradePromptProvider>
                <ThemedToaster />
                <PWAInstallPill />
                <PasskeyNudge />
                <Routes>
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/refer/:code" element={<ReferralLanding />} />
                    <Route path="/accept-invite" element={<AcceptInvite />} />

                    {/* Public compliance / payment routes — must be reachable
                        without auth for Razorpay manual review */}
                    <Route path="/legal/contact" element={<ContactPage />} />
                    <Route path="/legal/:slug" element={<LegalPage />} />
                    {/* Friendly aliases for short marketing URLs (SEO + nav typos).
                        These prevent silent fallback-to-/ which hides 404s. */}
                    <Route path="/contact" element={<Navigate to="/legal/contact" replace />} />
                    <Route path="/voices" element={<Navigate to="/#voices" replace />} />
                    <Route path="/modules" element={<Navigate to="/#modules" replace />} />
                    <Route path="/pricing" element={<PlanCompare />} />
                    <Route path="/pricing/legacy" element={<PricingPage />} />
                    <Route path="/pricing/compare" element={<PlanCompare />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/payment/success" element={<PaymentResultPage outcome="success" />} />
                    <Route path="/payment/failure" element={<PaymentResultPage outcome="failure" />} />

                    {/* Onboarding wizard (auth required, but outside DashboardLayout
                        so it has no sidebar / no gate loop) */}
                    <Route path="/onboarding" element={<Protected requireRole="tenant"><OnboardingWizard /></Protected>} />

                    {/* Tenant workspace */}
                    <Route path="/app" element={<Protected requireRole="tenant"><DashboardLayout mode="tenant" /></Protected>}>
                        <Route index element={<TenantDashboardV2 />} />
                        <Route path="dashboard-classic" element={<TenantOverview />} />
                        <Route path="clients" element={<Clients />} />
                        <Route path="cases" element={<Cases />} />
                        <Route path="cases/:id" element={<CaseDetail />} />
                        <Route path="hearings" element={<Hearings />} />
                        <Route path="cause-list" element={<CauseList />} />
                        <Route path="tasks" element={<Tasks />} />
                        <Route path="documents" element={<Documents />} />
                        <Route path="invoices" element={<Invoices />} />
                        <Route path="accounting" element={<TenantAccounting />} />
                        <Route path="communications" element={<Communications />} />
                        <Route path="chat" element={<Chat />} />
                        <Route path="wallet" element={<WalletPage />} />
                        <Route path="access-control" element={<AccessControl />} />
                        <Route path="my-week" element={<MyWeek />} />
                        <Route path="automations" element={<Automations />} />
                        <Route path="ai" element={<AIAssistant />} />
                        <Route path="staff" element={<Staff />} />
                        <Route path="productivity" element={<Productivity />} />
                        <Route path="calendar" element={<CalendarPage />} />
                        <Route path="court-vc-links" element={<CourtVcLinks />} />
                        <Route path="ai-docs" element={<AIDocs />} />
                        <Route path="analytics" element={<Analytics />} />
                        <Route path="compliance" element={<Compliance />} />
                        <Route path="audit" element={<AuditLog />} />
                        <Route path="referrals" element={<Referrals />} />
                        <Route path="ecourts" element={<EcourtsConfig />} />
                        <Route path="legal-search" element={<LegalSearch />} />
                        <Route path="data-integrity" element={<TenantDataIntegrity />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="settings/billing-automation" element={<BillingAutomation />} />
                        <Route path="sign-in-activity" element={<SignInActivity />} />
                        <Route path="billing/plans" element={<BillingPlans />} />
                        <Route path="billing/addons" element={<AppAddons />} />
                        <Route path="api-keys" element={<ApiKeysPage />} />
                        <Route path="white-label" element={<WhiteLabelPage />} />
                        <Route path="priority-support" element={<PrioritySupportPage />} />
                        <Route path="field-masks" element={<FieldMasksPage />} />
                        <Route path="settings/invoice-email" element={<InvoiceEmailSettingsPage />} />
                        <Route path="settings/invoice-template" element={<TenantInvoiceTemplate />} />
                        <Route path="settings/billing/invoices" element={<SubscriptionInvoices />} />
                        <Route path="notifications" element={<NotificationSettings scope="tenant" />} />
                        <Route path="push-settings" element={<TenantPushSettings />} />
                        <Route path="docs" element={<Documentation />} />
                    </Route>

                    {/* Super admin */}
                    <Route path="/admin" element={<Protected requireRole="super_admin"><DashboardLayout mode="admin" /></Protected>}>
                        <Route index element={<AdminOverview />} />
                        <Route path="tenants" element={<AdminTenants />} />
                        <Route path="tenants/:tenantId" element={<AdminTenantDetail />} />
                        <Route path="plans" element={<AdminPlans />} />
                        <Route path="topup-packs" element={<AdminTopupPacks />} />
                        <Route path="pricing-cms" element={<AdminPricingCMS />} />
                        <Route path="subscriptions" element={<AdminSubscriptions />} />
                        <Route path="features" element={<AdminFeatures />} />
                        <Route path="integrations" element={<AdminIntegrations />} />
                        <Route path="audit" element={<AdminAudit />} />
                        <Route path="comms" element={<AdminComms />} />
                        <Route path="sessions" element={<AdminSessions />} />
                        <Route path="delivery-chains" element={<AdminDeliveryChains />} />
                        <Route path="master-data" element={<AdminMasterData />} />
                        <Route path="court-vc-links" element={<AdminCourtVcLinks />} />
                        <Route path="referrals" element={<AdminReferrals />} />
                        <Route path="branding" element={<AdminBranding />} />
                        <Route path="ai-governance" element={<AdminAIGovernance />} />
                        <Route path="access-defaults" element={<AdminAccessDefaults />} />
                        <Route path="ecourts" element={<AdminEcourts />} />
                        <Route path="scraper" element={<AdminScraper />} />
                        <Route path="automations" element={<AdminAutomations />} />
                        <Route path="spend-analysis" element={<AdminSpendAnalysis />} />
                        <Route path="indiankanoon" element={<IndianKanoonConfig />} />
                        <Route path="voices" element={<VoicesConfig />} />
                        <Route path="chatbot" element={<AdminChatbot />} />
                        <Route path="legal" element={<AdminLegalPages />} />
                        <Route path="onboarding" element={<AdminOnboarding />} />
                        <Route path="accounting" element={<AdminAccounting />} />
                        <Route path="tenant-accounting" element={<AdminTenantAccounting />} />
                        <Route path="billing-automation" element={<AdminBillingAutomation />} />
                        <Route path="security" element={<AdminSecurity />} />
                        <Route path="notifications" element={<NotificationSettings scope="platform" />} />
                        <Route path="push-settings" element={<AdminPushSettings />} />
                        <Route path="system-health" element={<AdminSystemHealth />} />
                        <Route path="data-integrity" element={<AdminDataIntegrity />} />
                        <Route path="broadcast" element={<AdminBroadcast />} />
                        <Route path="backups" element={<AdminBackups />} />
                        <Route path="system" element={<AdminSystem />} />
                        <Route path="docs" element={<Documentation />} />
                        <Route path="documentation" element={<AdminDocumentation />} />
                        <Route path="case-types" element={<AdminCaseTypePrefixes />} />
                        <Route path="team" element={<AdminPlatformTeam />} />
                        <Route path="invoice-template" element={<AdminInvoiceTemplate />} />
                        <Route path="invoice-branding" element={<AdminPlatformInvoiceBranding />} />
                        <Route path="suggestion-rules" element={<AdminSuggestionRules />} />
                        <Route path="priority-support" element={<AdminPrioritySupport />} />
                        <Route path="enterprise" element={<AdminEnterpriseControls />} />
                    </Route>

                    {/* Client portal */}
                    <Route path="/portal" element={<Protected requireRole="portal"><PortalLayout /></Protected>}>
                        <Route index element={<PortalOverview />} />
                        <Route path="cases" element={<PortalCases />} />
                        <Route path="chat" element={<PortalChat />} />
                        <Route path="cases/:id" element={<PortalCaseDetail />} />
                        <Route path="hearings" element={<PortalHearings />} />
                        <Route path="invoices" element={<PortalInvoices />} />
                        <Route path="documents" element={<PortalDocuments />} />
                    </Route>

                    {/* ── Sales & Marketing Module (isolated) ──────────── */}
                    <Route path="/sm" element={<SMProtected><SMLayout /></SMProtected>}>
                        <Route index element={<SMDashboard />} />
                        <Route path="partners" element={<SMPartners />} />
                        <Route path="hierarchy" element={<SMHierarchy />} />
                        <Route path="commission-config" element={<SMCommissionConfig />} />
                        <Route path="commission-ledger" element={<SMCommissionLedger />} />
                        <Route path="ai-guide" element={<SMAIGuide />} />
                        <Route path="statements" element={<SMStatements />} />
                        <Route path="crm" element={<SMCRM />} />
                        <Route path="kyc" element={<SMKYC />} />
                        <Route path="referral-links" element={<SMReferralLinks />} />
                        <Route path="content-library" element={<SMDashboard />} />
                        <Route path="settings" element={<SMSettings />} />
                    </Route>
                    {/* ────────────────────────────────────────────────── */}

                    {/* Public join link landing */}
                    <Route path="/join/:shortCode" element={<JoinLink />} />

                    {/* Partner Portal — public pages */}
                    <Route path="/partner/register" element={<PartnerRegister />} />
                    <Route path="/partner/login" element={<PartnerLogin />} />

                    {/* Partner Portal — protected pages */}
                    <Route path="/partner" element={<PartnerProtected><PartnerLayout /></PartnerProtected>}>
                        <Route index element={<PartnerDashboard />} />
                        <Route path="commissions" element={<PartnerCommissions />} />
                        <Route path="referrals" element={<PartnerReferrals />} />
                        <Route path="links" element={<PartnerLinks />} />
                        <Route path="kyc-status" element={<PartnerKYCStatus />} />
                        <Route path="profile" element={<PartnerProfile />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <PasswordExpiryDialog />
                </UpgradePromptProvider>
            </BrowserRouter>
            </BrandingProvider>
            </AccessProvider>
            </FeaturesProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

function ThemedToaster() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    return (
        <Toaster theme={isDark ? "dark" : "light"} position="bottom-right" toastOptions={{
            style: {
                background: isDark ? "#171717" : "#FFFFFF",
                border: `1px solid ${isDark ? "#2A2A2A" : "#E5E2D8"}`,
                color: isDark ? "#FFFFFF" : "#1A1A1A",
                borderRadius: "2px", fontFamily: "Geist, sans-serif",
            },
        }}/>
    );
}
